import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { DataGrid } from '@/components/data-grid';
import type { ColumnFilterGroup } from '@/components/data-grid';
import { ROUTES } from '@/app/routes';
import type { ItemResponseDTO } from '@/api/generated/model';
import { useModerationList, useReportTypes } from '../api';
import { moderationColumns } from '../columns';

// Static filter options (doc 09 §4.1). Type/status are fixed code sets; violation comes from the
// getReportTypes endpoint. Filters are pushed to the server (contentType/reportType/reportStatus).
const TYPE_OPTIONS = [
  { key: 'P', label: 'Content' },
  { key: 'L', label: 'Live' },
  { key: 'C', label: 'Comment' },
  { key: 'U', label: 'User' },
  { key: 'M', label: 'Message' },
  { key: 'B', label: 'Feedback' },
];
const STATUS_OPTIONS = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'COMPLETED', label: 'Completed' },
];

/** Legacy user-report queue (GET /admin/api/moderation/items), reinstated as a dedicated page. */
export function ReportedPostsPage() {
  const navigate = useNavigate();
  const table = useModerationList();
  const reportTypes = useReportTypes();

  const filterGroups = useMemo<ColumnFilterGroup[]>(() => {
    // Backend getReportTypes returns [{ name: <code>, desc: <label> }] (ModerationServiceImpl);
    // the filter key is the code (== enum name) the server's reportType param expects.
    const violationOptions = (reportTypes.data ?? [])
      .map((rt) => ({ key: rt.name ?? '', label: rt.desc || rt.name || '' }))
      .filter((o) => o.key !== '');
    // Single-select: SearchRequestDTO accepts one enum per dimension.
    return [
      { columnKey: 'type', label: 'Type', options: TYPE_OPTIONS, multiSelect: false },
      { columnKey: 'reportType', label: 'Violation', options: violationOptions, multiSelect: false },
      { columnKey: 'status', label: 'Status', options: STATUS_OPTIONS, multiSelect: false },
    ];
  }, [reportTypes.data]);

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to={ROUTES.moderation} className="hover:text-foreground">
          Moderation
        </Link>
        <ChevronRight className="size-4" />
        <span className="text-foreground">User Reports</span>
      </nav>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">User Reports</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Posts, comments, users, and messages reported by the community.
        </p>
      </div>

      <div className="min-h-0 flex-1">
        <DataGrid<ItemResponseDTO>
          title="Reports"
          searchPlaceholder="Search reports…"
          columns={moderationColumns}
          rows={table.rows}
          total={table.total}
          isLoading={table.isLoading}
          isFetchingNextPage={table.isFetchingNextPage}
          isError={table.isError}
          error={table.error}
          endReached={table.endReached}
          onLoadMore={table.fetchNextPage}
          onRetry={() => void table.refetch()}
          search={table.search}
          onSearchChange={table.setSearch}
          filterGroups={filterGroups}
          selectedFilters={table.filters}
          onFiltersChange={table.setFilters}
          onRowClick={(row) => {
            if (row.id == null) return;
            // `:type` is advisory — the detail derives its real kind from the DTO (doc 09 §4.4).
            const type = (row.contentType ?? 'user').toLowerCase();
            void navigate(ROUTES.moderationDetails(String(row.id), type));
          }}
          getRowId={(row) => String(row.id ?? '')}
          emptyMessage="No reports"
        />
      </div>
    </div>
  );
}
